/**
 * Dashboard page
 */

// React
import React from 'react';

// Libraries
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

// Material UI
import { alpha, Box } from '@mui/material';

// Component
import AppBanner from 'components/AppBanner';

// Common styles
import {
  Content,
  Divider,
  HeaderContainer,
  RegisterButton,
  ServiceItem,
  ServicesContainer,
  Title,
  WrapperIcon,
  CustomGrid,
} from 'styles';

// Router
import ROUTES from 'configs/route';

// Recoil
import { isLoading } from 'recoil/LoadingRecoil';
import { userInfoAtom } from 'recoil/UserInfoRecoil';

// Utils
import { STORAGE_KEYS, FORM_BUTTON, AWS_S3 } from 'utils/constants';
import { handleGenerateAuthenCode, validateUserAccess } from 'utils/helper';
import { ServiceSystemProcessed } from 'utils/types/serviceType';

// Services
import useServicesAndBanners from 'hooks/useServicesAndBanner';

// Component
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const setIsLoading = useSetRecoilState(isLoading);
  const userInfo = useRecoilValue(userInfoAtom);

  const { banners, services } = useServicesAndBanners();

  const handleUserInfoButton = async (service: ServiceSystemProcessed) => {
    if (service?.statusText === FORM_BUTTON.START_UP) {
      setIsLoading(true);

      const isValidAccess = await validateUserAccess(service?.client_id, userInfo?.cognitoAuthSub);
      if (!isValidAccess) {
        navigate(ROUTES.NOT_FOUND);
        return;
      }

      const serviceUrl = await handleGenerateAuthenCode(
        service?.client_id,
        service?.redirect_uri,
        navigate
      );

      if (serviceUrl) {
        window.open(serviceUrl, '_blank');
      }
      setIsLoading(false);
    } else {
      localStorage.setItem(STORAGE_KEYS.SERVICE_SYSTEM_SELECTED, JSON.stringify(service));
      navigate(ROUTES.REGISTER_SYSTEM);
    }
  };

  return (
    <Box sx={{ paddingX: { xs: '20px', md: '50px' } }}>
      <AppBanner banners={banners} />
      <ServicesContainer>
        <CustomGrid
          servicesLength={services?.length || 0}
          container
          columnSpacing={{ xs: '10px', md: '20px' }}
          rowSpacing="20px"
        >
          {services?.map((service: any, index) => (
            <ServiceItem
              key={index}
              size={{ xs: 6, md: 3 }}
              borderTop={`solid ${service?.color}`}
              sx={{
                backgroundColor:
                  service?.statusText === FORM_BUTTON.START_UP ? alpha(service?.color, 0.1) : '',
              }}
            >
              <HeaderContainer>
                <WrapperIcon src={`${AWS_S3.BASE_URL}/${service?.icon}`} alt="Image service" />
                <Box>
                  <Title color={service?.color}>{service?.title}</Title>
                </Box>
              </HeaderContainer>
              <Divider />
              <Box>
                <Content>{service?.body}</Content>
              </Box>
              <RegisterButton
                disabled={service?.disabled}
                onClick={() => handleUserInfoButton(service)}
                variant={
                  service?.statusText === (FORM_BUTTON.START_UP || FORM_BUTTON.CURRENTLY_APPLYING)
                    ? 'contained'
                    : 'outlined'
                }
                color="secondary"
                sx={{
                  '&:hover': {
                    borderColor: service?.color,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'background.default',
                    color: 'text.secondary',
                  },
                }}
              >
                {service?.statusText || FORM_BUTTON.REGISTER_SERVICE}
              </RegisterButton>
            </ServiceItem>
          ))}
        </CustomGrid>
      </ServicesContainer>
    </Box>
  );
};

export default Dashboard;
